/**
 * Cart Slide Drawer - Right Side Sliding Cart
 * Custom component for SICOTAS
 * 
 * Listens for 'cart:open', 'sideCart:open', and 'ajaxProduct:added' events
 * to open the right-side sliding cart drawer.
 */

(function() {
  'use strict';

  const CartSlideDrawer = {
    drawer: null,
    overlay: null,
    closeBtn: null,
    content: null,
    isOpen: false,

    init() {
      this.drawer = document.getElementById('cart-slide-drawer');
      this.overlay = document.getElementById('cart-slide-overlay');
      this.closeBtn = document.getElementById('cart-slide-drawer-close');
      this.content = document.getElementById('cart-slide-drawer-content');
      this.itemsWrap = document.getElementById('cart-slide-drawer-items-wrap');

      if (!this.drawer) {
        console.warn('Cart Slide Drawer: Drawer element not found');
        return;
      }

      this.bindEvents();
      this.initRecommendationsScrollbar();
      this.initRecommendationSwatches();
      console.log('Cart Slide Drawer: Initialized');
    },

    bindEvents() {
      // Open events
      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('sideCart:open', () => this.open());
      
      // Listen for product added to cart - EComposer triggers this
      document.addEventListener('ajaxProduct:added', (e) => {
        this.refreshCart().then(() => this.open());
      });

      // Close events
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // ESC key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Quantity buttons
      this.drawer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-action]');
        if (!btn) return;

        const action = btn.dataset.action;
        const line = parseInt(btn.dataset.line, 10);
        const bundleId = btn.dataset.bundleId;

        if (action === 'increase') {
          // 支持普通商品和主商品Bundle
          let itemEl = this.drawer.querySelector(`.cart-slide-drawer__item[data-line="${line}"]`);
          if (!itemEl) {
            itemEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-line="${line}"]`);
          }
          
          const qtyInput = itemEl?.querySelector('.cart-slide-drawer__quantity-input');
          const currentQty = qtyInput ? parseInt(qtyInput.value, 10) : 0;
          
          // 检查30件限制
          if (currentQty >= 30) {
            this.showStockWarning(itemEl, 'Max 30 per order');
            return;
          }
          
          // 检查库存上限
          const maxQty = parseInt(btn.dataset.maxQty, 10);
          if (maxQty && !isNaN(maxQty) && currentQty >= maxQty) {
            this.showStockWarning(itemEl, `Max ${maxQty} in stock`);
            return;
          }
          
          this.updateQuantity(line, 1);
        } else if (action === 'decrease') {
          this.updateQuantity(line, -1);
        } else if (action === 'remove') {
          this.removeItem(line);
        } else if (action === 'increase-bundle' && bundleId) {
          // 检查30件限制
          const bundleEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-bundle-id="${bundleId}"]`);
          const bundleQtyInput = bundleEl?.querySelector('.cart-slide-drawer__bundle-quantity-input');
          const bundleCurrentQty = bundleQtyInput ? parseInt(bundleQtyInput.value, 10) : 0;
          
          if (bundleCurrentQty >= 30) {
            this.showBundleStockWarning(bundleEl, 'Max 30 per order');
            return;
          }
          
          this.updateBundleQuantity(bundleId, 1);
        } else if (action === 'decrease-bundle' && bundleId) {
          // 如果按钮被禁用（数量为1），不执行操作
          if (btn.classList.contains('is-disabled') || btn.disabled) {
            return;
          }
          this.updateBundleQuantity(bundleId, -1);
        } else if (action === 'remove-bundle' && bundleId) {
          this.removeBundleItems(bundleId);
        }
      });

      // Quantity input change
      this.drawer.addEventListener('change', (e) => {
        if (e.target.classList.contains('cart-slide-drawer__quantity-input')) {
          const line = parseInt(e.target.dataset.line, 10);
          let quantity = parseInt(e.target.value, 10);
          const itemEl = this.drawer.querySelector(`.cart-slide-drawer__item[data-line="${line}"]`);
          
          // 检查30件限制
          if (quantity > 30) {
            quantity = 30;
            e.target.value = 30;
            this.showStockWarning(itemEl, 'Max 30 per order');
          }
          
          if (quantity >= 1) {
            this.setQuantity(line, quantity);
          }
        } else if (e.target.classList.contains('cart-slide-drawer__bundle-quantity-input')) {
          const bundleId = e.target.dataset.bundleId;
          let quantity = parseInt(e.target.value, 10);
          const bundleEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-bundle-id="${bundleId}"]`);
          
          // 检查30件限制
          if (quantity > 30) {
            quantity = 30;
            e.target.value = 30;
            this.showBundleStockWarning(bundleEl, 'Max 30 per order');
          }
          
          if (bundleId && quantity >= 1) {
            this.setBundleQuantity(bundleId, quantity);
          }
        }
      });
      
      // Discount code apply button
      this.drawer.addEventListener('click', (e) => {
        if (e.target.classList.contains('cart-slide-drawer__discount-btn')) {
          this.applyDiscountCode();
        }
      });
      
      // Discount code input - Enter key to apply
      this.drawer.addEventListener('keydown', (e) => {
        if (e.target.classList.contains('cart-slide-drawer__discount-input') && e.key === 'Enter') {
          e.preventDefault();
          this.applyDiscountCode();
        }
      });
      
      // Clear error when user starts typing
      this.drawer.addEventListener('input', (e) => {
        if (e.target.classList.contains('cart-slide-drawer__discount-input')) {
          this.hideDiscountError();
        }
      });
    },

    open() {
      if (!this.drawer) return;

      this.isOpen = true;
      this.drawer.classList.add('is-active');
      this.drawer.setAttribute('aria-hidden', 'false');
      
      if (this.overlay) {
        this.overlay.classList.add('is-active');
      }

      document.body.classList.add('cart-slide-drawer-open');

      // Trap focus
      this.trapFocus();

      console.log('Cart Slide Drawer: Opened');
    },

    close() {
      if (!this.drawer) return;

      this.isOpen = false;
      this.drawer.classList.remove('is-active');
      this.drawer.setAttribute('aria-hidden', 'true');
      
      if (this.overlay) {
        this.overlay.classList.remove('is-active');
      }

      document.body.classList.remove('cart-slide-drawer-open');

      // Dispatch close event
      document.dispatchEvent(new CustomEvent('cart:close'));

      // If on cart page, reload to refresh the cart content
      if (window.location.pathname === '/cart' || window.location.pathname.includes('/cart')) {
        window.location.reload();
      }

      console.log('Cart Slide Drawer: Closed');
    },

    trapFocus() {
      // Simple focus trap - focus the close button when opened
      if (this.closeBtn) {
        setTimeout(() => this.closeBtn.focus(), 100);
      }
    },

    async refreshCart() {
      try {
        this.showLoading();
        
        const response = await fetch('/cart.js');
        const cart = await response.json();

        await this.renderCart(cart);
        
        // Update header cart count (使用计算后的显示数量)
        this.updateHeaderCartCount(this.calculateDisplayCount(cart));
        
        console.log('Cart Slide Drawer: Cart refreshed', cart);
      } catch (error) {
        console.error('Cart Slide Drawer: Error refreshing cart', error);
      }
    },

    showLoading() {
      if (this.itemsWrap) {
        this.itemsWrap.innerHTML = `
          <div class="cart-slide-drawer__loading">
            <div class="cart-slide-drawer__spinner"></div>
          </div>
        `;
      }
    },

    async renderCart(cart) {
      // 计算显示数量（bundle 按套计算）
      const displayCount = this.calculateDisplayCount(cart);
      
      // Update count (with parentheses to match HTML format)
      const countEl = document.getElementById('cart-slide-drawer-count');
      if (countEl) {
        countEl.textContent = '(' + displayCount + ')';
      }

      // Update subtotal (original total price before discounts)
      const subtotalEl = document.getElementById('cart-slide-drawer-subtotal');
      if (subtotalEl) {
        subtotalEl.textContent = this.formatMoney(cart.original_total_price);
      }

      // Re-fetch the drawer content from server for proper rendering
      let sectionData = null;
      try {
        const response = await fetch('/?section_id=cart-slide-drawer-content');
        const html = await response.text();
        
        // If section rendering is available, use it
        if (html && !html.includes('<!DOCTYPE')) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;
          
          // 读取 Liquid 计算好的数据
          const dataEl = tempDiv.querySelector('#cart-slide-drawer-data');
          if (dataEl) {
            sectionData = {
              correctSubtotal: parseInt(dataEl.dataset.correctSubtotal) || 0,
              discountAmount: parseInt(dataEl.dataset.discountAmount) || 0,
              youSaveAmount: parseInt(dataEl.dataset.youSaveAmount) || 0,
              totalPrice: parseInt(dataEl.dataset.totalPrice) || 0,
              itemCount: parseInt(dataEl.dataset.itemCount) || 0
            };
          }
          
          const newItemsWrap = tempDiv.querySelector('.cart-slide-drawer__items-wrap') || 
                            tempDiv.querySelector('#cart-slide-drawer-items-wrap');
          if (newItemsWrap && this.itemsWrap) {
            this.itemsWrap.innerHTML = newItemsWrap.innerHTML;
          }
        } else {
          // Fallback: render cart items manually
          this.renderCartItems(cart);
        }
      } catch (error) {
        // Fallback: render cart items manually
        this.renderCartItems(cart);
      }

      // Show/hide footer based on cart items
      const footer = document.getElementById('cart-slide-drawer-footer');
      if (footer) {
        // 优先使用 Section Rendering 的数据，否则用 cart.js 的数据
        const itemCount = sectionData ? sectionData.itemCount : cart.item_count;
        
        if (itemCount > 0) {
          footer.classList.remove('is-hidden');
          
          // 使用 Liquid 计算好的值（如果有），否则回退到 JS 计算
          let correctSubtotal, discountAmount, youSaveAmount, totalPrice;
          
          if (sectionData) {
            correctSubtotal = sectionData.correctSubtotal;
            discountAmount = sectionData.discountAmount;
            youSaveAmount = sectionData.youSaveAmount;
            totalPrice = sectionData.totalPrice;
          } else {
            // Fallback: 用 JS 计算（不太准确，但作为备用）
            const calcResult = this.calculateCorrectTotals(cart);
            correctSubtotal = calcResult.correctSubtotal;
            discountAmount = cart.original_total_price - cart.total_price;
            const productDiscount = correctSubtotal - cart.original_total_price;
            youSaveAmount = productDiscount + discountAmount;
            totalPrice = cart.total_price;
          }
          
          // 更新 Subtotal
          const subtotalPrice = document.getElementById('cart-slide-drawer-subtotal');
          if (subtotalPrice) {
            subtotalPrice.textContent = this.formatMoney(correctSubtotal);
          }
          
          // 更新 Total 行的价格
          const totalPriceEl = document.getElementById('cart-slide-drawer-total');
          if (totalPriceEl) {
            totalPriceEl.textContent = this.formatMoney(totalPrice);
          }
          // 更新 checkout 按钮上的价格
          const checkoutTotal = document.getElementById('cart-slide-drawer-checkout-total');
          if (checkoutTotal) {
            checkoutTotal.textContent = this.formatMoney(totalPrice);
          }
          
          // 更新 "Discount" 行
          let discountRow = document.getElementById('cart-slide-drawer-discount-row');
          if (discountRow) {
            if (discountAmount > 0) {
              discountRow.style.display = '';
              const discountValue = document.getElementById('cart-slide-drawer-discount-amount');
              if (discountValue) {
                discountValue.textContent = '-' + this.formatMoney(discountAmount);
              }
            } else {
              discountRow.style.display = 'none';
            }
          }
          
          // 更新 "You Save" 行
          let savingsRow = document.getElementById('cart-slide-drawer-savings');
          
          // 如果 savings-row 不存在但有折扣，则创建它
          if (!savingsRow && youSaveAmount > 0) {
            const totalRow = footer.querySelector('.cart-slide-drawer__total-row');
            if (totalRow) {
              savingsRow = document.createElement('div');
              savingsRow.className = 'cart-slide-drawer__savings-row';
              savingsRow.id = 'cart-slide-drawer-savings';
              savingsRow.innerHTML = `
                <span class="cart-slide-drawer__savings-label">You Save</span>
                <span class="cart-slide-drawer__savings-amount">-${this.formatMoney(youSaveAmount)}</span>
              `;
              totalRow.parentNode.insertBefore(savingsRow, totalRow);
            }
          } else if (savingsRow) {
            if (youSaveAmount > 0) {
              savingsRow.style.display = '';
              const savingsAmount = savingsRow.querySelector('.cart-slide-drawer__savings-amount');
              if (savingsAmount) {
                savingsAmount.textContent = '-' + this.formatMoney(youSaveAmount);
              }
            } else {
              savingsRow.style.display = 'none';
            }
          }
        } else {
          footer.classList.add('is-hidden');
        }
      }
      
      // 渲染折扣标签
      this.renderDiscountTags(cart);
    },
    
    // 渲染折扣标签（显示折扣码名称+该商品的折扣金额）
    renderDiscountTags(cart) {
      console.log('🔍 renderDiscountTags called, cart.items:', cart.items.length);
      cart.items.forEach(item => {
        console.log('🔍 Item:', item.product_title, 'discounts:', item.discounts);
        if (item.discounts && item.discounts.length > 0) {
          const container = document.querySelector(`.cart-slide-drawer__discount-tags-container[data-item-key="${item.key}"]`);
          console.log('🔍 Container found:', !!container, 'for key:', item.key);
          if (container) {
            // 生成折扣标签HTML（只显示折扣码名称）
            const tagsHTML = item.discounts.map(discount => `
              <span class="cart-slide-drawer__discount-tag">
                <svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" width="10" height="10">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 17 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"></path>
                </svg>
                ${discount.title}
              </span>
            `).join('');
            
            // 计算折扣金额（使用discount.amount）
            const totalDiscountAmount = item.discounts.reduce((sum, d) => sum + d.amount, 0);
            
            // 计算折扣百分比
            let discountPercent = 0;
            if (item.original_line_price > 0) {
              discountPercent = Math.round((totalDiscountAmount / item.original_line_price) * 100);
            }
            
            // 生成折扣金额HTML（格式：(-10% Save $30.00)）
            const discountAmountHTML = totalDiscountAmount > 0 
              ? `<div class="cart-slide-drawer__discount-amount-text">(-${discountPercent}% Save ${this.formatMoney(totalDiscountAmount)})</div>`
              : '';
            
            container.innerHTML = tagsHTML + discountAmountHTML;
          }
        }
      });
      
      // 更新主商品模式的价格显示
      this.updateBundleMainPrices(cart);
      
      // 更新普通商品的价格显示（处理compare_at_price和自动折扣）
      this.updateNormalItemPrices(cart);
    },
    
    // 更新主商品模式套装的价格显示（扣除自动折扣）
    updateBundleMainPrices(cart) {
      cart.items.forEach(item => {
        // 检查是否是主商品模式
        const props = item.properties || {};
        if (props._bundle_type === 'main') {
          // 计算自动折扣总额
          const autoDiscountTotal = item.discounts?.reduce((sum, d) => sum + d.amount, 0) || 0;
          // 真正的最终价格 = final_line_price - 自动折扣
          const realFinalPrice = item.final_line_price - autoDiscountTotal;
          
          // 更新价格显示
          const priceElement = document.querySelector(`.cart-slide-drawer__bundle[data-key="${item.key}"] .cart-slide-drawer__bundle-total`);
          if (priceElement) {
            priceElement.textContent = this.formatMoney(realFinalPrice);
          }
        }
      });
    },
    
    // 更新普通商品的价格显示
    // 注意：划线价和零售价由 Liquid 模板处理，JS 只更新最终支付金额
    updateNormalItemPrices(cart) {
      cart.items.forEach(item => {
        const props = item.properties || {};
        // 跳过套装商品
        if (props._bundle === 'true' || props._bundle_type === 'main') {
          return;
        }
        
        const itemElement = document.querySelector(`.cart-slide-drawer__item[data-key="${item.key}"]`);
        if (!itemElement) return;
        
        // 查找最终支付金额元素（通过 data-item-key 匹配）
        const finalPriceElement = itemElement.querySelector(`.cart-slide-drawer__item-price--final[data-item-key="${item.key}"]`);
        if (finalPriceElement) {
          // 检查是否有折扣（discounts 数组）
          const hasDiscount = item.discounts && item.discounts.length > 0;
          
          if (hasDiscount) {
            // 计算折扣总额
            const totalDiscountAmount = item.discounts.reduce((sum, d) => sum + d.amount, 0);
            // 最终金额 = 零售价(original_line_price) - 折扣总额（使用 Math.round 确保整数 cents）
            const actualFinalPrice = Math.round(item.original_line_price - totalDiscountAmount);
            
            finalPriceElement.textContent = this.formatMoney(actualFinalPrice);
            finalPriceElement.style.display = 'block';
          } else if (item.final_line_price < item.original_line_price) {
            // 其他类型折扣（非 discounts 数组）
            finalPriceElement.textContent = this.formatMoney(item.final_line_price);
            finalPriceElement.style.display = 'block';
          } else {
            // 没有折扣时隐藏
            finalPriceElement.style.display = 'none';
          }
        }
      });
    },
    
    // 计算正确的 Subtotal 和 Total（考虑套餐的真正划线价）
    calculateCorrectTotals(cart) {
      let correctSubtotal = 0;
      let correctTotal = 0;
      const processedBundleIds = new Set();
      
      for (const item of cart.items) {
        const props = item.properties || {};
        const bundleId = props._bundle_id;
        const isBundle = props._bundle === 'true';
        
        if (isBundle && bundleId) {
          // 套餐商品：检查是否已处理过这个 bundle
          if (!processedBundleIds.has(bundleId)) {
            // 收集这个 bundle 的信息
            let bundleMainComparePrice = 0;
            let bundleMainPrice = 0;
            let bundleTotalPrice = 0;
            let bundleQuantity = 1;
            
            for (const bundleItem of cart.items) {
              const biProps = bundleItem.properties || {};
              if (biProps._bundle_id === bundleId) {
                bundleTotalPrice += bundleItem.final_line_price;
                if (biProps._bundle_index === '0') {
                  bundleQuantity = bundleItem.quantity;
                }
                if (!bundleMainComparePrice && biProps._bundle_main_compare_price) {
                  bundleMainComparePrice = parseFloat(biProps._bundle_main_compare_price) || 0;
                }
                if (!bundleMainPrice && biProps._bundle_main_price) {
                  bundleMainPrice = parseFloat(biProps._bundle_main_price) || 0;
                }
              }
            }
            
            // 计算套餐的正确原价（使用 Math.round 确保整数 cents）
            let bundleOriginal = 0;
            if (bundleMainComparePrice > 0) {
              bundleOriginal = Math.round(bundleMainComparePrice * bundleQuantity);
            } else if (bundleMainPrice > 0) {
              bundleOriginal = Math.round(bundleMainPrice * bundleQuantity);
            }
            
            correctSubtotal += bundleOriginal;
            correctTotal += bundleTotalPrice;
            processedBundleIds.add(bundleId);
          }
        } else {
          // 普通商品：优先使用 variant.compare_at_price（Shopify API 返回的是 variant 级别的）
          const variantCompareAt = item.variant && item.variant.compare_at_price;
          if (variantCompareAt && variantCompareAt > item.price) {
            // compare_at_price 是单价，需要乘以数量（使用 Math.round 确保整数 cents）
            correctSubtotal += Math.round(variantCompareAt * item.quantity);
          } else {
            // 没有 compare_at_price，使用 original_line_price（已经是行价格）
            correctSubtotal += item.original_line_price;
          }
          correctTotal += item.final_line_price;
        }
      }
      
      return { correctSubtotal, correctTotal };
    },

    renderCartItems(cart) {
      if (!this.itemsWrap) return;

      if (cart.item_count === 0) {
        this.itemsWrap.innerHTML = `
          <div class="cart-slide-drawer__empty">
            <h2 class="cart-slide-drawer__empty-title">Hi! Your shopping cart is empty.</h2>
            <p class="cart-slide-drawer__empty-text">If you have an account, sign in to see items added on earlier visits</p>
            <div class="cart-slide-drawer__empty-actions">
              <a href="/account/login" class="cart-slide-drawer__empty-btn cart-slide-drawer__empty-btn--login">Sign In</a>
              <a href="/collections/all" class="cart-slide-drawer__empty-btn cart-slide-drawer__empty-btn--continue">Continue Shopping</a>
            </div>
          </div>
        `;
        return;
      }

      // 收集所有 bundle_id 并去重
      const renderedBundleIds = new Set();
      let itemsHtml = '<ul class="cart-slide-drawer__items" id="cart-slide-drawer-items">';
      
      cart.items.forEach((item, index) => {
        const line = index + 1;
        const isBundle = item.properties && item.properties._bundle === 'true';
        const bundleId = item.properties ? item.properties._bundle_id : null;
        
        if (isBundle && bundleId) {
          // 如果已渲染过这个 bundle，跳过
          if (renderedBundleIds.has(bundleId)) return;
          renderedBundleIds.add(bundleId);
          
          // 渲染 bundle
          itemsHtml += this.renderBundleItem(cart, bundleId);
        } else {
          // 渲染普通商品
          itemsHtml += this.renderNormalItem(item, line);
        }
      });

      itemsHtml += '</ul>';
      this.itemsWrap.innerHTML = itemsHtml;
    },

    renderNormalItem(item, line) {
      const imageUrl = item.image ? item.image.replace(/(\.[^.]+)$/, '_160x$1') : '';
      
      // 构建选项 HTML - Quantity 和 Color 分两行显示
      let optionsHtml = `<div class="cart-slide-drawer__item-options">`;
      optionsHtml += `<div class="cart-slide-drawer__item-option">Quantity: ${item.quantity} Piece</div>`;
      if (item.variant_title && item.variant_title !== 'Default Title') {
        // 解析 options_with_values，显示所有选项
        if (item.options_with_values && item.options_with_values.length > 0) {
          item.options_with_values.forEach((opt) => {
            optionsHtml += `<div class="cart-slide-drawer__item-option">${opt.name}: ${opt.value}</div>`;
          });
        } else {
          // 如果没有 options_with_values，使用 variant_title 显示为 Color
          optionsHtml += `<div class="cart-slide-drawer__item-option">Color: ${item.variant_title}</div>`;
        }
      }
      optionsHtml += `</div>`;

      return `
        <li class="cart-slide-drawer__item" data-line="${line}" data-key="${item.key}">
          <div class="cart-slide-drawer__item-image">
            ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(item.title)}" loading="lazy">` : ''}
          </div>
          <div class="cart-slide-drawer__item-details">
            <div class="cart-slide-drawer__item-row">
              <div class="cart-slide-drawer__item-info">
                <div class="cart-slide-drawer__item-title">${this.escapeHtml(item.product_title)}</div>
                ${optionsHtml}
              </div>
              <button type="button" class="cart-slide-drawer__item-remove" data-action="remove" data-line="${line}" aria-label="Remove">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_remove_${line})">
                    <path d="M3 3.33333V14.6667H13V3.33333H3Z" stroke="#666666" stroke-width="1.2" stroke-linejoin="round"/>
                    <path d="M6.66675 6.66667V11" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9.33325 6.66667V11" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M1.33325 3.33333H14.6666" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M5.33325 3.33333L6.42959 1.33333H9.59229L10.6666 3.33333H5.33325Z" stroke="#666666" stroke-width="1.2" stroke-linejoin="round"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_remove_${line}">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
            <div class="cart-slide-drawer__item-meta-row">
              <div class="cart-slide-drawer__item-qty-col">
                <div class="cart-slide-drawer__item-qtywrap">
                  <button type="button" class="cart-slide-drawer__quantity-btn${item.quantity <= 1 ? ' is-disabled' : ''}" data-action="decrease" data-line="${line}" aria-label="Decrease quantity"${item.quantity <= 1 ? ' disabled' : ''}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0.599609 4.9751H9.34961" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  <input type="number" class="cart-slide-drawer__quantity-input" data-line="${line}" value="${item.quantity}" min="1" max="30" aria-label="Quantity">
                  <button type="button" class="cart-slide-drawer__quantity-btn${item.quantity >= 30 ? ' is-disabled' : ''}" data-action="increase" data-line="${line}" aria-label="Increase quantity"${item.quantity >= 30 ? ' disabled' : ''}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.99581 0.600098L4.98438 9.3501" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M0.599609 4.9751H9.34961" stroke="#666666" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="cart-slide-drawer__item-price-wrap">
                <div class="cart-slide-drawer__item-price-row">
                  ${item.original_line_price !== item.final_line_price ? `<span class="cart-slide-drawer__item-price-original">${this.formatMoney(item.original_line_price)}</span>` : ''}
                  <span class="cart-slide-drawer__item-price${item.original_line_price !== item.final_line_price ? ' cart-slide-drawer__item-price--sale' : ''}">${this.formatMoney(item.final_line_price)}</span>
                </div>
                ${item.original_line_price !== item.final_line_price ? `<div class="cart-slide-drawer__item-discount">(-${Math.round((item.original_line_price - item.final_line_price) / item.original_line_price * 100)}% Save ${this.formatMoney(item.original_line_price - item.final_line_price)})</div>` : ''}
              </div>
            </div>
          </div>
        </li>
      `;
    },

    renderBundleItem(cart, bundleId) {
      // 收集该 bundle 的所有商品
      const bundleItems = cart.items.filter(item => 
        item.properties && item.properties._bundle_id === bundleId
      );
      
      if (bundleItems.length === 0) return '';
      
      // 获取主商品信息
      const firstItem = bundleItems[0];
      const mainTitle = firstItem.properties._bundle_main_title || 'Bundle Set';
      const mainImage = firstItem.properties._bundle_main_image || (firstItem.image ? firstItem.image.replace(/(\.[^.]+)$/, '_160x$1') : '');
      const mainUrl = firstItem.properties._bundle_main_url || firstItem.url;
      const mainComparePrice = parseInt(firstItem.properties._bundle_main_compare_price || '0', 10);
      const bundleQuantity = firstItem.quantity;
      
      // 计算总价和原价
      let bundleTotal = 0;
      let bundleOriginalTotal = 0;
      bundleItems.forEach(item => {
        bundleTotal += item.final_line_price;
        bundleOriginalTotal += item.original_line_price;
      });
      
      // 如果有主商品的 compare_at_price，用它作为划线价
      if (mainComparePrice > 0) {
        bundleOriginalTotal = mainComparePrice * bundleQuantity;
      }
      
      // 判断是否有折扣
      const hasDiscount = bundleOriginalTotal > bundleTotal;
      let discountPercent = 0;
      let savings = 0;
      if (hasDiscount) {
        savings = bundleOriginalTotal - bundleTotal;
        discountPercent = Math.round((savings / bundleOriginalTotal) * 100);
      }
      
      // 渲染子商品
      let childrenHtml = '';
      bundleItems.forEach(item => {
        const childImage = item.image ? item.image.replace(/(\.[^.]+)$/, '_80x$1') : '';
        const childVariant = item.variant_title && item.variant_title !== 'Default Title' 
          ? `<div class="cart-slide-drawer__bundle-child-variant">${this.escapeHtml(item.variant_title)}</div>` 
          : '<div class="cart-slide-drawer__bundle-child-variant"></div>';
        
        childrenHtml += `
          <div class="cart-slide-drawer__bundle-child" data-key="${item.key}">
            <div class="cart-slide-drawer__bundle-child-image">
              ${childImage ? `<img src="${childImage}" alt="${this.escapeHtml(item.product_title)}" loading="lazy">` : ''}
            </div>
            <div class="cart-slide-drawer__bundle-child-info">
              <div class="cart-slide-drawer__bundle-child-title">${this.escapeHtml(item.product_title)}</div>
              <div class="cart-slide-drawer__bundle-child-meta">
                ${childVariant}
                <div class="cart-slide-drawer__bundle-child-price">${this.formatMoney(item.price)}</div>
              </div>
            </div>
          </div>
        `;
      });
      
      // 价格显示HTML
      const priceHtml = hasDiscount 
        ? `<div class="cart-slide-drawer__bundle-price-wrap">
            <div class="cart-slide-drawer__bundle-price-row">
              <span class="cart-slide-drawer__bundle-price-original">${this.formatMoney(bundleOriginalTotal)}</span>
              <span class="cart-slide-drawer__bundle-total cart-slide-drawer__bundle-total--sale">${this.formatMoney(bundleTotal)}</span>
            </div>
            <div class="cart-slide-drawer__bundle-discount">(-${discountPercent}% Save ${this.formatMoney(savings)})</div>
          </div>`
        : `<div class="cart-slide-drawer__bundle-price-wrap">
            <div class="cart-slide-drawer__bundle-price-row">
              <span class="cart-slide-drawer__bundle-total">${this.formatMoney(bundleTotal)}</span>
            </div>
          </div>`;
      
      return `
        <li class="cart-slide-drawer__bundle" data-bundle-id="${bundleId}">
          <div class="cart-slide-drawer__bundle-header">
            <div class="cart-slide-drawer__bundle-image">
              ${mainImage ? `<img src="${mainImage}" alt="${this.escapeHtml(mainTitle)}" loading="lazy">` : ''}
            </div>
            <div class="cart-slide-drawer__bundle-info">
              <div class="cart-slide-drawer__bundle-info-top">
                <div class="cart-slide-drawer__bundle-info-left">
                  <div class="cart-slide-drawer__bundle-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2 6.89 2 8V19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4Z" fill="currentColor"/>
                    </svg>
                    Bundle · ${bundleItems.length} items
                  </div>
                  <a href="${mainUrl}" class="cart-slide-drawer__bundle-title">${this.escapeHtml(mainTitle)}</a>
                  <div class="cart-slide-drawer__bundle-options">
                    <div class="cart-slide-drawer__bundle-option">Quantity: ${bundleQuantity} Piece</div>
                    ${firstItem && firstItem.options_with_values ? firstItem.options_with_values.map(opt => `<div class="cart-slide-drawer__bundle-option">${opt.name}: ${opt.value}</div>`).join('') : ''}
                  </div>
                </div>
                <button type="button" class="cart-slide-drawer__bundle-remove" data-action="remove-bundle" data-bundle-id="${bundleId}" aria-label="Remove Bundle">
                  <img src="${this.drawer.dataset.removeIcon || ''}" alt="Remove" width="16" height="16">
                </button>
              </div>
              <div class="cart-slide-drawer__bundle-meta-row">
                <div class="cart-slide-drawer__bundle-qty-col">
                  <div class="cart-slide-drawer__bundle-qty">
                    <button type="button" class="cart-slide-drawer__quantity-btn${bundleQuantity <= 1 ? ' is-disabled' : ''}" data-action="decrease-bundle" data-bundle-id="${bundleId}" aria-label="Decrease quantity"${bundleQuantity <= 1 ? ' disabled' : ''}>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.599609 4.9751H9.34961" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <input type="number" class="cart-slide-drawer__bundle-quantity-input" data-bundle-id="${bundleId}" value="${bundleQuantity}" min="1" max="30" aria-label="Bundle Quantity">
                    <button type="button" class="cart-slide-drawer__quantity-btn${bundleQuantity >= 30 ? ' is-disabled' : ''}" data-action="increase-bundle" data-bundle-id="${bundleId}" aria-label="Increase quantity"${bundleQuantity >= 30 ? ' disabled' : ''}>
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.99581 0.600098L4.98438 9.3501" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M0.599609 4.9751H9.34961" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
                ${priceHtml}
              </div>
            </div>
          </div>
          <div class="cart-slide-drawer__bundle-items">
            ${childrenHtml}
          </div>
        </li>
      `;
    },

    async updateQuantity(line, change) {
      // 查找该行商品的数量显示元素（支持普通商品和主商品Bundle）
      let itemEl = this.drawer.querySelector(`.cart-slide-drawer__item[data-line="${line}"]`);
      if (!itemEl) {
        itemEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-line="${line}"]`);
      }
      if (!itemEl) return;

      // 支持 input 和 span 两种元素
      const qtyInput = itemEl.querySelector('.cart-slide-drawer__quantity-input');
      const qtyValue = itemEl.querySelector('.cart-slide-drawer__quantity-value');
      
      let currentQty;
      if (qtyInput) {
        currentQty = parseInt(qtyInput.value, 10);
      } else if (qtyValue) {
        currentQty = parseInt(qtyValue.textContent, 10);
      } else {
        return;
      }

      const newQty = Math.max(0, currentQty + change);

      await this.setQuantity(line, newQty, itemEl);
    },

    async setQuantity(line, quantity, itemEl = null) {
      try {
        // 单个商品 loading
        if (itemEl) {
          this.showItemLoading(itemEl);
        } else {
          // 如果没有传入 itemEl，尝试查找（支持普通商品和主商品Bundle）
          itemEl = this.drawer.querySelector(`.cart-slide-drawer__item[data-line="${line}"]`);
          if (!itemEl) {
            itemEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-line="${line}"]`);
          }
          if (itemEl) {
            this.showItemLoading(itemEl);
          }
        }

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: line,
            quantity: quantity
          })
        });

        const cart = await response.json();
        
        // 检查 API 是否返回错误（库存不足等）
        if (cart.status === 422 || cart.description) {
          // 显示库存不足警告
          this.showStockWarning(itemEl, cart.description || 'Insufficient stock');
          if (itemEl) {
            this.hideItemLoading(itemEl);
          }
          // 刷新购物车以获取正确状态
          await this.refreshCart();
          return;
        }
        
        await this.renderCart(cart);

        // Update header cart count if exists (使用计算后的显示数量)
        this.updateHeaderCartCount(this.calculateDisplayCount(cart));

        // Dispatch cart change event
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart } }));

      } catch (error) {
        console.error('Cart Slide Drawer: Error updating quantity', error);
        if (itemEl) {
          this.hideItemLoading(itemEl);
        }
        this.refreshCart();
      }
    },
    
    // 显示库存警告toast
    showStockWarning(itemEl, message) {
      this.showToast(message);
    },
    
    // 显示Bundle库存警告toast
    showBundleStockWarning(bundleEl, message) {
      this.showToast(message);
    },
    
    // 折扣码应用功能
    async applyDiscountCode() {
      const input = this.drawer.querySelector('.cart-slide-drawer__discount-input');
      const btn = this.drawer.querySelector('.cart-slide-drawer__discount-btn');
      if (!input) return;
      
      const code = input.value.trim();
      
      // 检查是否输入了折扣码
      if (!code) {
        this.showDiscountError('Please enter a discount code');
        return;
      }
      
      // 显示loading状态
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Applying...';
      }
      
      try {
        // 访问 /discount/CODE 页面，Shopify 会将折扣码存入 cookie
        await fetch('/discount/' + encodeURIComponent(code), {
          redirect: 'follow'
        });
        
        // 刷新购物车以获取最新状态
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        // 检查购物车是否有折扣应用
        const hasCartDiscount = cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0;
        const hasLineDiscount = cart.items && cart.items.some(item => 
          item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0
        );
        
        if (hasCartDiscount || hasLineDiscount) {
          this.showDiscountSuccess('Discount code applied!');
          input.value = '';
          await this.renderCart(cart);
        } else {
          // 折扣码无效或不适用
          this.showDiscountError('Invalid or inapplicable discount code');
        }
      } catch (error) {
        console.error('Cart Slide Drawer: Error applying discount code', error);
        this.showDiscountError('Failed to apply discount code');
      } finally {
        // 恢复按钮状态
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Apply';
        }
      }
    },
    
    // 显示折扣码错误提示
    showDiscountError(message) {
      let errorEl = this.drawer.querySelector('.cart-slide-drawer__discount-error');
      const form = this.drawer.querySelector('.cart-slide-drawer__discount-form');
      
      if (!errorEl && form) {
        errorEl = document.createElement('div');
        errorEl.className = 'cart-slide-drawer__discount-error';
        form.insertAdjacentElement('afterend', errorEl);
      }
      
      if (errorEl) {
        errorEl.textContent = message;
        errorEl.classList.add('is-visible');
      }
      
      // 给输入框添加错误样式
      const input = this.drawer.querySelector('.cart-slide-drawer__discount-input');
      if (input) {
        input.classList.add('is-error');
      }
    },
    
    // 隐藏折扣码错误提示
    hideDiscountError() {
      const errorEl = this.drawer.querySelector('.cart-slide-drawer__discount-error');
      if (errorEl) {
        errorEl.classList.remove('is-visible');
      }
      
      const input = this.drawer.querySelector('.cart-slide-drawer__discount-input');
      if (input) {
        input.classList.remove('is-error');
      }
    },
    
    // 显示折扣码成功提示
    showDiscountSuccess(message) {
      let successEl = this.drawer.querySelector('.cart-slide-drawer__discount-success');
      const form = this.drawer.querySelector('.cart-slide-drawer__discount-form');
      
      if (!successEl && form) {
        successEl = document.createElement('div');
        successEl.className = 'cart-slide-drawer__discount-success';
        form.insertAdjacentElement('afterend', successEl);
      }
      
      if (successEl) {
        successEl.textContent = message;
        successEl.classList.add('is-visible');
        
        // 3秒后隐藏
        setTimeout(() => {
          successEl.classList.remove('is-visible');
        }, 3000);
      }
    },
    
    // 显示全局toast提示
    showToast(message) {
      let toast = document.querySelector('.cart-slide-drawer__toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'cart-slide-drawer__toast';
        document.body.appendChild(toast);
      }
      
      toast.textContent = message;
      toast.classList.add('is-visible');
      
      // 3秒后隐藏
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 3000);
    },

    async removeItem(line) {
      // 支持普通商品和主商品Bundle
      let itemEl = this.drawer.querySelector(`.cart-slide-drawer__item[data-line="${line}"]`);
      if (!itemEl) {
        itemEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-line="${line}"]`);
      }
      await this.setQuantity(line, 0, itemEl);
    },

    // 显示单个商品 loading
    showItemLoading(itemEl) {
      if (!itemEl) return;
      itemEl.classList.add('is-loading');
    },

    // 隐藏单个商品 loading
    hideItemLoading(itemEl) {
      if (!itemEl) return;
      itemEl.classList.remove('is-loading');
    },

    // Bundle 操作方法
    async updateBundleQuantity(bundleId, change) {
      try {
        // 单个 Bundle loading
        const bundleEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-bundle-id="${bundleId}"]`);
        if (bundleEl) {
          bundleEl.classList.add('is-loading');
        }
        
        // 获取当前购物车
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        // 找到所有属于该 bundle 的商品
        const bundleItems = cart.items.filter(item => {
          return item.properties && item.properties._bundle_id === bundleId;
        });
        
        if (bundleItems.length === 0) return;
        
        // 获取当前数量
        const currentQty = bundleItems[0].quantity;
        const newQty = Math.max(0, currentQty + change);
        
        // 批量更新所有 bundle 商品的数量
        const updates = {};
        bundleItems.forEach(item => {
          updates[item.key] = newQty;
        });
        
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates })
        });
        
        const updatedCart = await response.json();
        await this.renderCart(updatedCart);
        
        // Update header cart count (使用计算后的显示数量)
        this.updateHeaderCartCount(this.calculateDisplayCount(updatedCart));
        
        // Dispatch cart change event
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: updatedCart } }));
        
      } catch (error) {
        console.error('Cart Slide Drawer: Error updating bundle quantity', error);
        this.refreshCart();
      }
    },

    async removeBundleItems(bundleId) {
      await this.updateBundleQuantity(bundleId, -999999); // 将数量设为0
    },

    async setBundleQuantity(bundleId, newQty) {
      try {
        // 单个 Bundle loading
        const bundleEl = this.drawer.querySelector(`.cart-slide-drawer__bundle[data-bundle-id="${bundleId}"]`);
        if (bundleEl) {
          bundleEl.classList.add('is-loading');
        }
        
        // 获取当前购物车
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        // 找到所有属于该 bundle 的商品
        const bundleItems = cart.items.filter(item => {
          return item.properties && item.properties._bundle_id === bundleId;
        });
        
        if (bundleItems.length === 0) return;
        
        // 确保数量至少为1
        const quantity = Math.max(1, newQty);
        
        // 批量更新所有 bundle 商品的数量
        const updates = {};
        bundleItems.forEach(item => {
          updates[item.key] = quantity;
        });
        
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates })
        });
        
        const updatedCart = await response.json();
        await this.renderCart(updatedCart);
        
        // Update header cart count (使用计算后的显示数量)
        this.updateHeaderCartCount(this.calculateDisplayCount(updatedCart));
        
        // Dispatch cart change event
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: updatedCart } }));
        
      } catch (error) {
        console.error('Cart Slide Drawer: Error setting bundle quantity', error);
        this.refreshCart();
      }
    },

    updateHeaderCartCount(count) {
      // Update various cart count elements in the header
      const countElements = document.querySelectorAll(
        '.cart-link__bubble-num, .cart-count, [data-cart-count], .js-cart-count'
      );
      countElements.forEach(el => {
        el.textContent = count;
      });

      // Handle cart bubble visibility
      const bubbles = document.querySelectorAll('.cart-link__bubble');
      bubbles.forEach(bubble => {
        if (count > 0) {
          bubble.classList.add('cart-link__bubble--visible');
        } else {
          bubble.classList.remove('cart-link__bubble--visible');
        }
      });
    },

    // 计算购物车显示数量：普通商品数量 + bundle套数（不是子商品数量）
    calculateDisplayCount(cart) {
      let displayCount = 0;
      const countedBundleIds = new Set();
      
      cart.items.forEach(item => {
        const bundleId = item.properties && item.properties._bundle_id;
        
        if (bundleId) {
          // 是 bundle 子商品，检查是否已计算过该 bundle
          if (!countedBundleIds.has(bundleId)) {
            // 第一次遇到该 bundle，计入 bundle 数量（按套数）
            displayCount += item.quantity;
            countedBundleIds.add(bundleId);
          }
        } else {
          // 普通商品，直接计入数量
          displayCount += item.quantity;
        }
      });
      
      return displayCount;
    },

    formatMoney(cents) {
      // Simple money formatting - adjust based on store's currency format
      const amount = (cents / 100).toFixed(2);
      return '$' + amount;
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    // Initialize custom scrollbar for recommendations
    initRecommendationsScrollbar() {
      const container = document.getElementById('cart-slide-drawer-rec-grid');
      const thumb = document.querySelector('.cart-slide-drawer__scrollbar-thumb');
      const track = document.querySelector('.cart-slide-drawer__scrollbar-track');

      if (!container || !thumb || !track) return;

      const self = this;
      let isDragging = false;
      let startX, startLeft;

      // Update thumb position and width based on scroll
      function updateThumb() {
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;
        const scrollLeft = container.scrollLeft;
        const trackWidth = track.offsetWidth;

        // Hide scrollbar if content fits
        if (scrollWidth <= clientWidth) {
          thumb.style.display = 'none';
          return;
        } else {
          thumb.style.display = 'block';
        }

        // Calculate thumb width proportionally
        const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 40);
        thumb.style.width = thumbWidth + 'px';

        // Calculate thumb position
        const maxScrollLeft = scrollWidth - clientWidth;
        const maxThumbLeft = trackWidth - thumbWidth;
        const thumbLeft = maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * maxThumbLeft : 0;
        thumb.style.left = thumbLeft + 'px';
      }

      // Start dragging
      function startDrag(clientX) {
        isDragging = true;
        startX = clientX;
        startLeft = thumb.offsetLeft;
        document.body.style.userSelect = 'none';
        thumb.style.cursor = 'grabbing';
      }

      // During drag
      function doDrag(clientX) {
        if (!isDragging) return;

        const trackWidth = track.offsetWidth;
        const thumbWidth = thumb.offsetWidth;

        let newLeft = startLeft + (clientX - startX);
        newLeft = Math.max(0, Math.min(newLeft, trackWidth - thumbWidth));

        thumb.style.left = newLeft + 'px';

        // Sync scroll position
        const scrollRatio = newLeft / (trackWidth - thumbWidth);
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        container.scrollLeft = scrollRatio * maxScrollLeft;
      }

      // End dragging
      function endDrag() {
        if (!isDragging) return;
        isDragging = false;
        document.body.style.userSelect = '';
        thumb.style.cursor = 'grab';
      }

      // Mouse events
      thumb.addEventListener('mousedown', (e) => {
        e.preventDefault();
        startDrag(e.clientX);
      });

      document.addEventListener('mousemove', (e) => {
        if (isDragging) doDrag(e.clientX);
      });

      document.addEventListener('mouseup', endDrag);

      // Touch events for mobile
      thumb.addEventListener('touchstart', (e) => {
        startDrag(e.touches[0].clientX);
      }, { passive: true });

      document.addEventListener('touchmove', (e) => {
        if (isDragging) {
          doDrag(e.touches[0].clientX);
        }
      }, { passive: true });

      document.addEventListener('touchend', endDrag);

      // Sync thumb on scroll
      container.addEventListener('scroll', updateThumb);

      // Update on window resize
      window.addEventListener('resize', updateThumb);

      // Initial update
      updateThumb();

      // Store reference for later re-init if needed
      this.recScrollbar = { container, thumb, track, updateThumb };
    },

    // Initialize recommendation swatches - click to switch image
    initRecommendationSwatches() {
      const recContainer = document.getElementById('cart-slide-drawer-recommendations');
      if (!recContainer) return;

      // Handle swatch clicks
      recContainer.addEventListener('click', (e) => {
        const swatch = e.target.closest('.cart-slide-drawer__rec-swatch');
        if (swatch) {
          e.preventDefault();
          e.stopPropagation();
          
          const recItem = swatch.closest('.cart-slide-drawer__rec-item');
          const mainImg = recItem.querySelector('.cart-slide-drawer__rec-main-img');
          const variantImage = swatch.dataset.variantImage;
          
          // Update active state
          recItem.querySelectorAll('.cart-slide-drawer__rec-swatch').forEach(s => s.classList.remove('is-active'));
          swatch.classList.add('is-active');
          
          // Switch image if variant has image
          if (variantImage && mainImg) {
            mainImg.src = variantImage;
          }
          return;
        }

        // Handle product card click (navigate to product page)
        const recItem = e.target.closest('.cart-slide-drawer__rec-item');
        if (recItem && !e.target.closest('.cart-slide-drawer__rec-swatch')) {
          const productUrl = recItem.dataset.productUrl;
          if (productUrl) {
            window.location.href = productUrl;
          }
        }
      });
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CartSlideDrawer.init());
  } else {
    CartSlideDrawer.init();
  }

  // Export for external access
  window.CartSlideDrawer = CartSlideDrawer;
})();
